import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Check, CheckCheck, Clock3, Mail, Send } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getChatMessages, getChatThreads, sendChatReply } from '../../Services/APIs/chat';
import { getCurrentCompanyId, joinChatRoom, leaveChatRoom, logSocketEvent } from '../../Services/socket/chatSocket';
import { sampleMessagesByThread, sampleThreads } from './mockData';
import { useSocket } from '../../Context/SocketContext';
import {
    buildThreadPreviewFromMessage,
    normalizeMessagePayload,
    USE_CHAT_MOCKS,
    getDisplayName,
    getThreadEmail,
    normalizeMessagesPayload,
    normalizeThreadPayload,
    normalizeThreadsPayload,
    sortMessages,
    sortThreads,
    updateMessageDelivery,
    upsertMessage
} from './helpers';

const formatMessageTime = (value) => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).format(date);
};

const renderMessageStatus = (message) => {
    const status = message?.status || 'sent';

    if (status === 'failed') {
        return <AlertCircle className="h-3.5 w-3.5 text-[#B42318]" />;
    }

    if (status === 'pending') {
        return <Clock3 className="h-3.5 w-3.5 text-[#8A5A00]" />;
    }

    if (status === 'delivered') {
        return message?.is_read ? (
            <CheckCheck className="h-3.5 w-3.5 text-[#718096]" />
        ) : (
            <CheckCheck className="h-3.5 w-3.5 text-[#718096]" />
        );
    }

    return <Check className="h-3.5 w-3.5 text-[#718096]" />;
};

const MessageDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { chatToken } = useParams();
    const { socket } = useSocket();
    const [thread, setThread] = useState(location.state?.thread || null);
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState('');
    const [threadLoading, setThreadLoading] = useState(!location.state?.thread && Boolean(chatToken));
    const [messagesLoading, setMessagesLoading] = useState(true);
    const [sendingReply, setSendingReply] = useState(false);
    const [threadError, setThreadError] = useState('');
    const [messagesError, setMessagesError] = useState('');
    const messagesEndRef = useRef(null);

    const sortedMessages = useMemo(() => sortMessages(messages), [messages]);

    useEffect(() => {
        if (messagesLoading) return;

        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
        });
    }, [messagesLoading, sortedMessages]);

    useEffect(() => {
        const loadThread = async () => {
            if (thread || !chatToken) return;

            setThreadLoading(true);
            setThreadError('');

            try {
                if (USE_CHAT_MOCKS) {
                    const matchedThread = sampleThreads.find((item) => item.chatToken === chatToken) || null;
                    setThread(matchedThread);
                    if (!matchedThread) {
                        setThreadError('Thread not found');
                    }
                    return;
                }

                const companyId = getCurrentCompanyId() || null;
                if (!companyId) {
                    setThreadError('Company ID not found');
                    return;
                }

                const response = await getChatThreads({
                    company: companyId,
                    page: 1,
                    limit: 50
                });
                const matchedThread =
                    sortThreads(normalizeThreadsPayload(response)).find((item) => item.chatToken === chatToken) || null;

                setThread(matchedThread);
                if (!matchedThread) {
                    setThreadError('Thread not found');
                }
            } catch (error) {
                console.error('Error loading thread details:', error);
                setThreadError(error?.response?.data?.message || error?.message || 'Failed to load thread');
            } finally {
                setThreadLoading(false);
            }
        };

        loadThread();
    }, [chatToken, thread]);

    useEffect(() => {
        if (USE_CHAT_MOCKS || !chatToken || !socket) return undefined;

        const handleConnect = () => {
            logSocketEvent('connected', socket.id);
            joinChatRoom(socket, chatToken);
        };
        const handleDisconnect = (reason) => {
            logSocketEvent('disconnected', reason);
        };
        const handleJoinedChat = (payload) => {
            logSocketEvent('joinedChat', payload);
            setMessagesError('');
        };
        const handleLeftChat = (payload) => {
            logSocketEvent('leftChat', payload);
        };
        const handleChatMessage = (message) => {
            logSocketEvent('chatMessage', message);

            const incomingMessage = normalizeMessagePayload(message);

            if (incomingMessage?.chatToken !== chatToken) return;

            setMessages((current) => upsertMessage(current, incomingMessage));
            setThread((current) =>
                current
                    ? {
                        ...current,
                        ...buildThreadPreviewFromMessage(incomingMessage)
                    }
                    : current
            );
        };
        const handleMessageDelivered = (payload) => {
            logSocketEvent('messageDelivered', payload);

            if (payload?.chatToken !== chatToken) return;

            setMessages((current) => updateMessageDelivery(current, payload));
            setThread((current) =>
                current
                    ? {
                        ...current,
                        lastMessageStatus: payload?.status || current?.lastMessageStatus
                    }
                    : current
            );
        };
        const handleChatThreadUpdated = (payload) => {
            logSocketEvent('chatThreadUpdated', payload);

            const updatedThread = normalizeThreadPayload(payload);

            if (updatedThread?.chatToken !== chatToken) return;

            setThread((current) => ({
                ...(current || {}),
                ...updatedThread
            }));
        };
        const handleSocketError = (error) => {
            logSocketEvent('socketError', error);
            console.error('Socket error:', error);
            setMessagesError(error?.message || 'Realtime connection error');
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('joinedChat', handleJoinedChat);
        socket.on('leftChat', handleLeftChat);
        socket.on('chatMessage', handleChatMessage);
        socket.on('messageDelivered', handleMessageDelivered);
        socket.on('chatThreadUpdated', handleChatThreadUpdated);
        socket.on('socketError', handleSocketError);

        if (socket.connected) {
            handleConnect();
        }

        return () => {
            leaveChatRoom(socket, chatToken);
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('joinedChat', handleJoinedChat);
            socket.off('leftChat', handleLeftChat);
            socket.off('chatMessage', handleChatMessage);
            socket.off('messageDelivered', handleMessageDelivered);
            socket.off('chatThreadUpdated', handleChatThreadUpdated);
            socket.off('socketError', handleSocketError);
        };
    }, [chatToken, socket]);

    useEffect(() => {
        const loadMessages = async () => {
            if (!chatToken) {
                setMessagesError('Chat token is missing');
                setMessages([]);
                setMessagesLoading(false);
                return;
            }

            setMessagesLoading(true);
            setMessagesError('');

            try {
                if (USE_CHAT_MOCKS) {
                    setMessages(sampleMessagesByThread[chatToken] || []);
                    return;
                }

                const response = await getChatMessages(chatToken);
                setMessages(normalizeMessagesPayload(response));
            } catch (error) {
                console.error('Error loading chat messages:', error);
                setMessages([]);
                setMessagesError(error?.response?.data?.message || error?.message || 'Failed to load messages');
            } finally {
                setMessagesLoading(false);
            }
        };

        loadMessages();
    }, [chatToken]);

    const handleSendReply = async () => {
        if (!thread || !replyText.trim()) return;

        const trimmedMessage = replyText.trim();
        setSendingReply(true);
        setMessagesError('');

        try {
            let newMessage;

            if (USE_CHAT_MOCKS) {
                newMessage = {
                    _id: `mock-${Date.now()}`,
                    chatToken: thread.chatToken,
                    message: trimmedMessage,
                    direction: 'outbound',
                    sentBy: thread?.createdBy?.name || 'Admin',
                    sesMessageId: `mock-${Date.now()}`,
                    status: 'sent',
                    is_read: true,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
            } else {
                const response = await sendChatReply({
                    chatToken: thread.chatToken,
                    message: trimmedMessage,
                    sentBy: thread?.createdBy?.name || 'Admin'
                });
                const savedMessage = normalizeMessagePayload(response);
                const responseSesMessageId =
                    savedMessage?.sesMessageId ||
                    response?.sesMessageId ||
                    response?.data?.sesMessageId ||
                    response?.messageId ||
                    response?._id ||
                    null;

                newMessage = {
                    _id: savedMessage?._id || response?.messageId || responseSesMessageId || `local-${Date.now()}`,
                    chatToken: thread.chatToken,
                    message: trimmedMessage,
                    direction: 'outbound',
                    sentBy: thread?.createdBy?.name || 'Admin',
                    sesMessageId: responseSesMessageId,
                    status: savedMessage?.status || 'sent',
                    is_read: savedMessage?.is_read ?? true,
                    createdAt: savedMessage?.createdAt || new Date().toISOString(),
                    updatedAt: savedMessage?.updatedAt || new Date().toISOString()
                };
            }

            setMessages((current) => upsertMessage(current, newMessage));
            setThread((current) =>
                current
                    ? {
                        ...current,
                        ...buildThreadPreviewFromMessage(newMessage)
                    }
                    : current
            );
            setReplyText('');
        } catch (error) {
            console.error('Error sending chat reply:', error);
            setMessagesError(error?.response?.data?.message || error?.message || 'Failed to send reply');
        } finally {
            setSendingReply(false);
        }
    };

    const handleComposerKeyDown = (event) => {
        if (event.key === 'Enter' && event.ctrlKey) {
            event.preventDefault();
            handleSendReply();
        }
    };

    return (
        <main className="flex-1 overflow-hidden bg-[#F7F7F8]">
            <div className="mx-auto h-full max-w-5xl p-4 md:p-6 xl:p-8">
                <div className="flex h-[calc(100dvh-2.5rem)] min-h-[560px] flex-col rounded-2xl border border-[#E8E6E3] bg-white">
                    <div className="border-b border-[#E8E6E3] px-6 py-6">
                        {threadLoading ? (
                            <p className="text-sm text-[#6B6B6B]">Loading thread...</p>
                        ) : threadError ? (
                            <p className="text-sm text-[#B42318]">{threadError}</p>
                        ) : thread ? (
                            <>
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="flex items-start gap-3">
                                        <button
                                            onClick={() => navigate('/dashboard/messages')}
                                            className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F5F3F0] text-[#6B6B6B] transition hover:bg-[#ECE8E3] hover:text-[#2D2D2D]"
                                        >
                                            <ArrowLeft className="h-4 w-4" />
                                        </button>

                                        <div>
                                            <h1 className="text-2xl font-semibold text-[#2D2D2D]">{thread?.subject || 'No subject'}</h1>
                                            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6B6B6B]">
                                                <span className="font-medium text-[#2D2D2D]">{getDisplayName(thread)}</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <Mail className="h-4 w-4" />
                                                    {getThreadEmail(thread)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="text-sm text-[#6B6B6B]">No thread selected.</p>
                        )}
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto bg-[#F7F7F8] px-4 py-5 md:px-6">
                        {messagesLoading ? (
                            <div className="space-y-4">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <div
                                        key={index}
                                        className={`animate-pulse ${index % 2 === 0 ? 'mr-auto' : 'ml-auto'} w-full max-w-2xl rounded-2xl bg-white p-4`}
                                    >
                                        <div className="h-3 w-1/4 rounded bg-[#EFEDE9]" />
                                        <div className="mt-3 h-3 w-full rounded bg-[#F5F3F0]" />
                                        <div className="mt-2 h-3 w-4/5 rounded bg-[#F5F3F0]" />
                                    </div>
                                ))}
                            </div>
                        ) : messagesError ? (
                            <p className="text-sm text-[#B42318]">{messagesError}</p>
                        ) : sortedMessages.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-[#D8D4CF] bg-white px-6 py-12 text-center">
                                <p className="text-base text-[#2D2D2D]">No messages in this thread.</p>
                                <p className="mt-2 text-sm text-[#6B6B6B]">This conversation has not started yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {sortedMessages.map((message) => {
                                    const isOutbound = message?.direction === 'outbound';

                                    return (
                                        <div key={message._id} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                                            <div
                                                className={`max-w-[82%] rounded-2xl px-4 py-2.5 shadow-sm ${isOutbound
                                                    ? 'rounded-br-md bg-[#C6D2CB] text-[#223028]'
                                                    : 'rounded-bl-md border border-[#E8E6E3] bg-white text-[#2D2D2D]'
                                                    }`}
                                            >

                                                <p className="whitespace-pre-wrap text-sm leading-6">{message?.message}</p>
                                                <div className="mt-1.5 flex items-center justify-end gap-1.5">
                                                    <span className="text-[11px] text-[#6B6B6B]">{formatMessageTime(message?.createdAt)}</span>
                                                    {isOutbound && renderMessageStatus(message)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </div>

                    <div className="border-t border-[#E8E6E3] bg-white px-4 py-3 md:px-6">
                        <div className="flex items-end gap-3 rounded-2xl border border-[#E8E6E3] bg-[#FCFBF9] p-2.5 md:p-3">
                            <textarea
                                value={replyText}
                                onChange={(event) => setReplyText(event.target.value)}
                                onKeyDown={handleComposerKeyDown}
                                placeholder="Type your reply here..."
                                rows={1}
                                disabled={!thread || sendingReply}
                                className="max-h-24 min-h-[42px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-[#2D2D2D] outline-none placeholder:text-[#9A958E] disabled:cursor-not-allowed disabled:opacity-60"
                            />

                            <button
                                onClick={handleSendReply}
                                disabled={!thread || sendingReply || !replyText.trim()}
                                className="inline-flex h-[42px] shrink-0 items-center gap-2 rounded-xl bg-[#38433A] px-4 text-sm font-medium text-white transition hover:bg-[#2D352F] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Send className="h-4 w-4" />
                                {sendingReply ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default MessageDetail;
